import { z } from "zod"
import { useState } from "react"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { setToken, trpc } from "@accubrew/state"

import { Button, AuthLayout, AuthCard } from "../../components"
import { useNavigate } from "@tanstack/react-location"
import ResetBg from "../../assets/forgetpasswordBg.svg"
const schema = z
  .object({
    password: z
      .string()
      .min(8, "Password must be at least 8 characters")
      .regex(/[A-Z]/, "Password must include at least 1 uppercase letter")
      .regex(/[0-9]/, "Password must include at least 1 number")
      .min(1, "Password is required"),
    confirm: z.string().min(8),
  })
  .superRefine((data, ctx) => {
    if (data.password !== data.confirm) {
      ctx.addIssue({ code: "custom", message: "Passwords don't match" })
    }
  })

const resolver = zodResolver(schema)

const __DEV__ = import.meta.env.DEV

export function ResetPassword() {
  const context = trpc.useContext()
  const navigate = useNavigate()
  const [{ code, email }] = useState(() => {
    const searchParams = new URLSearchParams(location.search)
    return {
      email: searchParams.get("email"),
      code: searchParams.get("code"),
    }
  })

  const resetPassword = trpc.user.resetPassword.useMutation()
  const verifyResetPasswordCode = trpc.user.verifyResetPasswordCode.useQuery(
    { code: code!, email: email! }, //
    { enabled: Boolean(code && email) },
  )

  const { handleSubmit, formState, register } = useForm<z.infer<typeof schema>>({
    resolver,
    defaultValues: __DEV__
      ? { password: "", confirm: "" } //
      : { password: "", confirm: "" },

    mode: "onChange",
  })

  const { isValid } = formState

  const handleSubmitPress = handleSubmit((data) => {
    resetPassword.mutate(
      { code: code!, email: email!, password: data.password },
      {
        onSuccess(data) {
          // setToken(data.token)
          // context.user.get.setData(undefined, data.user)
          // TODO
          // requestAnimationFrame(() => {/
            navigate({ to: "/auth/login" })
          // })
        },
      },
    )
  })

  if (verifyResetPasswordCode.isLoading) {
    return (
      <AuthLayout title="Please Wait..." backgroundImg={ResetBg}>
        <AuthCard title="Please Wait...">
          <svg
            aria-hidden="true"
            className="mx-auto my-4 w-8 h-8  animate-spin text-[#F88D2A] fill-[#FEEEDF]"
            viewBox="0 0 100 101"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
              fill="currentColor"
            />
            <path
              d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
              fill="currentFill"
            />
          </svg>
        </AuthCard>
      </AuthLayout>
    )
  }

  if (verifyResetPasswordCode.isError) {
    return (
      <AuthLayout title="Something Went Wrong.">
        <AuthCard title="Something Went Wrong." className="pb-4">
          <p>please try again later.</p>
        </AuthCard>
      </AuthLayout>
    )
  }

  return (
    <AuthLayout title="Reset Password">
      <AuthCard title="Reset Password">
        <div className="mb-8 text-[#342F20] text-[18px]">{/*   */}</div>
        <form className="mb-3 pb-14" onSubmit={handleSubmitPress}>
          <label htmlFor="password" className="block mb-1 font-[800] text-[14px] text-[#B6B4AF]">
            Password
          </label>
          <div className="border mb-5 rounded-[6px]">
            <input type="password" id="password" className="w-full border-none bg-transparent" {...register("password")} />
          </div>
          <label htmlFor="confirm" className="block mb-1 font-[800] text-[14px] text-[#B6B4AF]">
            Confirm Password
          </label>
          <div className="border mb-5 rounded-[6px]">
            <input type="password" id="confirm" className="w-full border-none bg-transparent" {...register("confirm")} />
          </div>
          <ul className="list-disc p-4 text-[#B6B4AF] font-[16px]">
            <li>Password should be at least 8 characters</li>
            <li>Password should include at least 1 uppercase letter, 1 number</li>
          </ul>
          <Button
            title="Save"
            variant="primary"
            className="py-3 mb-15 block w-full text-[18px] font-[800]"
            disabled={!isValid}
            isLoading={resetPassword.isLoading}
          />
        </form>
      </AuthCard>
    </AuthLayout>
  )
}
